import {IllustratedMessage} from '@ui/images/illustrated-message';
import {Trans} from '@ui/i18n/trans';
import {TrackTable} from '@app/web-player/tracks/track-table/track-table';
import React, {Fragment, useContext} from 'react';
import {queueGroupId} from '@app/web-player/queue-group-id';
import {useAlbum} from '@app/web-player/albums/requests/use-album';
import {
  actionButtonClassName,
  MediaPageHeaderLayout,
  MediaBadges,
} from '@app/web-player/layout/media-page-header-layout';
import {AvatarGroup} from '@ui/avatar/avatar-group';
import {Avatar} from '@ui/avatar/avatar';
import {FormattedDuration} from '@ui/i18n/formatted-duration';
import {PlaybackToggleButton} from '@app/web-player/playable-item/playback-toggle-button';
import {AlbumImage} from '@app/web-player/albums/album-image/album-image';
import {
  Album,
  getAlbumRouteAlbumId,
  getDerivedAlbumId,
  getDerivedAlbumTrack,
  isDerivedAlbum,
} from '@app/web-player/albums/album';
import {getSmallArtistImage} from '@app/web-player/artists/artist-image/small-artist-image';
import {getArtistLink} from '@app/web-player/artists/artist-link';
import {FormattedDate} from '@ui/i18n/formatted-date';
import {useSortableTableData} from '@common/ui/tables/use-sortable-table-data';
import {BulletSeparatedItems} from '@app/web-player/layout/bullet-separated-items';
import {CommentList} from '@common/comments/comment-list/comment-list';
import {useAlbumPermissions} from '@app/web-player/albums/use-album-permissions';
import {PageStatus} from '@common/http/page-status';
import {PageMetaTags} from '@common/http/page-meta-tags';
import {TrackActionsBar} from '@app/web-player/tracks/track-actions-bar';
import {FocusScope} from '@react-aria/focus';
import {ChipList} from '@ui/forms/input-field/chip-field/chip-list';
import {Chip} from '@ui/forms/input-field/chip-field/chip';
import {Link} from 'react-router-dom';
import {CommentBarContextProvider} from '@app/web-player/tracks/waveform/comment-bar-context';
import {CommentBarNewCommentForm} from '@app/web-player/tracks/waveform/comment-bar-new-comment-form';
import {AdHost} from '@common/admin/ads/ad-host';
import {useCommentPermissions} from '@app/web-player/tracks/hooks/use-comment-permissions';
import {TruncatedDescription} from '@common/ui/other/truncated-description';
import {useLogEvent, useLogEventProps} from '@common/core/event-logger';
import {AdPageItem} from '@app/web-player/ads/ad-page-item';
import {GenreLink} from '@app/web-player/genres/genre-link';
import {useUserRolesAccess} from '@common/auth/user-roles-access';
import {useAuth} from '@common/auth/use-auth';
import {createTrackPlayFilter} from '@app/web-player/tracks/utils/track-filter';
import {
  addAlbumFlags,
  EnhancedAlbum,
} from '@app/web-player/albums/utils/album-filter';
import {MediaItemStats} from '../tracks/media-item-stats';
import {isMobileApp} from '@common/core/platform-detection';
import clsx from 'clsx';
import {NotificationDialogTrigger} from '@common/notifications/dialog/notification-dialog-trigger';
import ChannelNavbar from '../channels/channel-navbar';
import {DialogContext} from '@ui/overlays/dialog/dialog-context';
import {useIsMobileMediaQuery} from '@ui/utils/hooks/is-mobile-media-query';
import {useTrackPermissions} from '@app/web-player/tracks/hooks/use-track-permissions';

export function AlbumPage({album}: any) {
  const {canView: showComments, canCreate: allowCommenting} =
    useCommentPermissions();
  const query = useAlbum(
    album
      ? {
          loader: 'albumPage',
          albumId: getAlbumRouteAlbumId(album),
          derivedId: getDerivedAlbumId(album),
        }
      : {loader: 'albumPage'},
  );
  const pageAlbum = query.data?.album;
  const derivedTrack = getDerivedAlbumTrack(pageAlbum);
  const derivedCommentable =
    isDerivedAlbum(pageAlbum) && derivedTrack ? derivedTrack : pageAlbum;
  const {canEdit: canEditAlbum} = useAlbumPermissions(pageAlbum);
  const {canEdit: canEditTrack} = useTrackPermissions([derivedTrack]);
  const canEdit =
    isDerivedAlbum(pageAlbum) && derivedTrack ? canEditTrack : canEditAlbum;
  const ctx = useContext(DialogContext);

  const logEntity =
    isDerivedAlbum(pageAlbum) && derivedTrack ? derivedTrack : pageAlbum;
  const eventPayload: useLogEventProps = {
    event_type: 'view',
    entity_id: logEntity?.id,
    entity_type: logEntity?.model_type ?? 'album',
    metadata: {
      ...pageAlbum,
    },
  };
  const isMobile = useIsMobileMediaQuery();
  const isNativeApp = isMobileApp();

  useLogEvent(eventPayload); // ✅ Appelle la fonction normale ici

  if (query.data) {
    return (
      <>
        {!ctx && isMobile && (
          <ChannelNavbar
            fixed={true}
            background={isNativeApp ? 'transparent' : 'bg'}
            className={isNativeApp ? 'app-ambient-channel-header' : undefined}
          >
            <div
              className={clsx(
                'mb-16 flex items-center justify-end gap-24 md:mb-20',
              )}
            >
              {isMobile ? (
                <div className="flex flex-shrink-0 items-end gap-2">
                  <NotificationDialogTrigger />
                </div>
              ) : null}
            </div>
          </ChannelNavbar>
        )}
        <div className={!ctx && isMobile ? 'mt-64' : ''}>
          <PageMetaTags query={query} />
          <AdHost slot="general_top" className="mb-44" />
          <AlbumPageHeader album={query.data.album} />
          {query.data.album.tags?.length ? (
            <FocusScope>
              <ChipList className="mb-16" selectable>
                {query.data.album.tags.map(tag => (
                  <Chip elementType={Link} to={`/tag/${tag.name}`} key={tag.id}>
                    #{tag.display_name || tag.name}
                  </Chip>
                ))}
              </ChipList>
            </FocusScope>
          ) : null}
          <TruncatedDescription
            description={query.data.album.description}
            className="mt-24 text-sm"
          />
          <AdHost slot="album_above" className="mt-34" />
          <AlbumTrackTable album={query.data.album} />
          <AdPageItem object="album" className={'mt-44'}></AdPageItem>
          <CommentBarContextProvider>
            {allowCommenting && derivedCommentable ? (
              <CommentBarNewCommentForm
                className="mb-16 mt-30"
                commentable={derivedCommentable}
              />
            ) : null}
          </CommentBarContextProvider>
          {showComments && derivedCommentable && (
            <CommentList
              className="mt-34"
              commentable={derivedCommentable}
              canDeleteAllComments={canEdit}
            />
          )}
          <AdHost slot="general_bottom" className="mt-44" />
        </div>
      </>
    );
  }

  return (
    <PageStatus
      query={query}
      loaderClassName="absolute inset-0 m-auto"
      loaderIsScreen={false}
    />
  );
}

interface AlbumTrackTableProps {
  album: Album;
}
function AlbumTrackTable({album}: AlbumTrackTableProps) {
  const {data, sortDescriptor, onSortChange} = useSortableTableData(
    album.tracks,
  );
  return (
    <div className="mt-44">
      <TrackTable
        queueGroupId={queueGroupId(album)}
        tracks={data}
        sortDescriptor={sortDescriptor}
        onSortChange={onSortChange}
        hideTrackImage
        hideArtist
        hideAlbum
        hidePopularity={false}
      />
      {!album.tracks?.length ? (
        <IllustratedMessage
          className="mt-34"
          title={<Trans message="Nothing to display" />}
          description={
            <Trans message="This album does not have any tracks yet" />
          }
        />
      ) : null}
    </div>
  );
}

interface PlaylistPageHeaderProps {
  album: Album;
}
function AlbumPageHeader({album}: PlaylistPageHeaderProps) {
  const isDerived = isDerivedAlbum(album);
  const derivedTrack = getDerivedAlbumTrack(album);
  const statsItem =
    isDerived && derivedTrack ? derivedTrack : album;
  const {managesAlbum} = useAlbumPermissions(album);
  const managesActionItem = isDerived ? false : managesAlbum;
  const totalDuration = album.tracks?.reduce(
    (t, track) => t + (track.duration || 0),
    0,
  );
  const {user} = useAuth();
  const {userAccess} = useUserRolesAccess();
  const trackPlayFilterOptions = {
    permissions: userAccess.medias.play,
    userId: user?.id,
  };
  const playTrackFilter = createTrackPlayFilter(trackPlayFilterOptions);

  const albumTrackAvailable = {
    ...album,
    tracks: album?.tracks?.filter(playTrackFilter),
  };

  let localAlbum: Album & EnhancedAlbum = addAlbumFlags(album);
  const hasExclusiveReleaseWindow =
    !!localAlbum.k_release_date &&
    !!localAlbum.release_date &&
    new Date(localAlbum.k_release_date) < new Date(localAlbum.release_date);

  return (
    <Fragment>
      <MediaPageHeaderLayout
        className="mb-28"
        image={<AlbumImage album={album} className="rounded" />}
        title={localAlbum.name}
        badges={
          <MediaBadges
            isAvailable={localAlbum.isAvailable}
            isEncoded={localAlbum.isEncoded}
            isReleased={localAlbum.isReleased}
            isExclusivity={localAlbum.isExclusivity}
          />
        }
        subtitle={
          <AvatarGroup>
            {localAlbum.artists?.map(artist => (
              <Avatar
                key={artist.id}
                artist={artist}
                circle
                src={getSmallArtistImage(artist)}
                label={artist.name}
                link={getArtistLink(artist)}
              />
            ))}
          </AvatarGroup>
        }
        description={
          <BulletSeparatedItems className="text-sm text-muted">
            {[
              localAlbum.tracks?.length && (
                <Trans
                  key="trackCount"
                  message="[one 1 track|other :count tracks]"
                  values={{count: localAlbum.tracks.length}}
                />
              ),
              localAlbum.tracks?.length && (
                <FormattedDuration key="duration" ms={totalDuration} verbose />
              ),
              hasExclusiveReleaseWindow && (
                <del key="originalDate">
                  <FormattedDate date={localAlbum.release_date} />
                </del>
              ),
              hasExclusiveReleaseWindow && localAlbum.releaseDate && (
                <strong key="exclusiveDate">
                  <FormattedDate date={localAlbum.releaseDate} />
                </strong>
              ),
              !hasExclusiveReleaseWindow &&
                localAlbum.isReleased &&
                localAlbum.releaseDate && (
                  <FormattedDate
                    key="releasedDate"
                    date={localAlbum.releaseDate}
                  />
                ),
              !hasExclusiveReleaseWindow &&
                !localAlbum.isReleased &&
                localAlbum.releaseDate && (
                  <strong key="unreleasedDate">
                    <FormattedDate date={localAlbum.releaseDate} />
                  </strong>
                ),
            ].filter(Boolean)}
            <MediaItemStats item={statsItem} />
          </BulletSeparatedItems>
        }
        description2={
          <>
            {(localAlbum.genres ?? []).length > 0 && (
              <ChipList
                size="xs"
                color="chip"
                radius="rounded-full"
                className="justify-center md:justify-start"
              >
                {(localAlbum.genres ?? []).map(genre => (
                  <Chip key={genre.id}>
                    <GenreLink genre={genre} />
                  </Chip>
                ))}
              </ChipList>
            )}
          </>
        }
        actionButtons={
          <TrackActionsBar
            item={album}
            managesItem={managesActionItem}
            likeableItem={isDerived ? derivedTrack : undefined}
            repostItem={isDerived ? derivedTrack : undefined}
            buttonGap={undefined}
            buttonSize="sm"
            buttonRadius="rounded-full"
            buttonClassName={actionButtonClassName()}
          >
            <PlaybackToggleButton
              disabled={!albumTrackAvailable.tracks?.length}
              buttonType="text"
              queueId={queueGroupId(albumTrackAvailable)}
              className={actionButtonClassName({isFirst: true})}
            />
          </TrackActionsBar>
        }
      />
    </Fragment>
  );
}
